import React from 'react';
import clsx from 'clsx';
import { TypedMemo } from '../../../common/types';
/* eslint-disable css-modules/no-unused-class -- a shared stylesheet; each part uses its own slice of it */
import styles from './Panel.module.scss';
/* eslint-enable css-modules/no-unused-class */

export { PanelHead } from './PanelHead';
export type { PanelHeadProps } from './PanelHead';
export { PanelBody } from './PanelBody';
export type { PanelBodyProps } from './PanelBody';
export { PanelFoot } from './PanelFoot';
export type { PanelFootProps } from './PanelFoot';

type PanelElement = 'div' | 'section' | 'article' | 'aside' | 'nav' | 'li';

export interface PanelProps extends Omit<React.HTMLAttributes<HTMLElement>, 'className' | 'children'> {
  className?: string;
  /** The HTML element of the box. The default is a div. */
  as?: PanelElement;
  /**
   * A box inside another box (a row, a file, an option card, a tile). It has a border and a
   * smaller radius, but no shadow. Never stack two shadows.
   */
  nested?: boolean;
  /**
   * `danger` gives the border the colour of a destructive action. `brand` gives it the brand
   * colour, for a one-time box that must stand out (the new API token).
   */
  tone?: 'default' | 'danger' | 'brand';
  /** Adds the inner padding of a panel with no head. With a PanelHead, put the content in a PanelBody. */
  pad?: boolean;
  children: React.ReactNode;
}

/**
 * The one box of the Music24 app. Every card and panel uses it, so all boxes look the same.
 * A top-level box has the border, the large radius and the shadow. A nested box has no shadow.
 * Put a PanelHead first, the content in a PanelBody, and an optional PanelFoot last.
 */
const PanelComponent: React.FC<PanelProps> = ({
  className,
  as: Element = 'div',
  nested = false,
  tone = 'default',
  pad = false,
  children,
  ...rest
}) => (
  <Element
    // eslint-disable-next-line react/jsx-props-no-spreading -- the box passes its HTML attributes (data-testid, role, onClick) to the element
    {...rest}
    className={clsx(
      styles.panel,
      nested && styles.nested,
      tone === 'danger' && styles.danger,
      tone === 'brand' && styles.brand,
      pad && styles.pad,
      className
    )}
  >
    {children}
  </Element>
);

export const Panel = TypedMemo(PanelComponent);
